import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Icon } from "semantic-ui-react";
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useLoginCheck } from "../../../../hooks/useLoginCheck";
import "../../../../styles/scss/recital2027PickupAudition.scss";

/**
 * 発表会2027（SLASH GALLERY vol.5）ピックアップオーディション
 * 「当日の順番・審査結果」管理ページ。
 *
 * 設計方針:
 * - 番号(no=ゼッケン)と並び順(sort_order=出演順)を分離。表示・時間は並び順で決まる。
 * - 1次タブ = 全員を並び順(sort_order)昇順で表示。ドラッグで並べ替え。
 *     ・番号未確定(未ロック): 並びに合わせて no と sort_order を一緒に振り直す。
 *     ・番号確定後(ロック中): sort_order だけを振り直す（ゼッケン no は固定）。
 *   1次は4人ずつ同時に審査するため、上から4人ずつが1組。時間は DB に持たず
 *   10:30 +(組-1)分 で算出する（公開側と同じルール）。4行ごとに区切り線を入れて組を示す。
 * - 2次タブ = 1次結果 pass の人だけを並び順のまま表示（再採番なし。公開側と一致）。
 * - 合否はセル即時保存（楽観更新 + 失敗時ロールバック）。2次はティア(Tier1〜3)を廃止し合否のみ。
 * - ゼッケン印刷後の番号ズレ防止のため「番号確定ロック」を用意。確定後も並べ替えは可能で、
 *   その場合は番号を固定したまま当日の順番・時間だけが変わる。ロック状態は admin 側で保持。
 * - このページは recital2027_pickup_auditions の no/sort_order/first/second を編集するだけで、
 *   公開側 /recital2027/pickup/timetable に自動反映される。
 */

const apiBase = process.env.REACT_APP_API_URL;

// ゼッケン番号(no)は3桁ゼロパディング表示（公開側の {no:03d} と揃える）。
const pad3 = (no) => String(no ?? "").padStart(3, "0");

// 1次は4レーン（横4人）で同時に審査し、1組=1分で進む。開始 10:00（公開側の
// PICKUP_ORDER_FIRST_START_MIN と同値）。
//
// sort_order は「4レーングリッドのセル絶対位置」(1始まり) で、
//   組   = (sort_order - 1) / GROUP_SIZE   （0始まり）
//   レーン = (sort_order - 1) % GROUP_SIZE + 1（1列目〜4列目）
// を表す。参加者が4の倍数でないぶん空きセルがあり、そのぶん sort_order に欠番が出る
// （＝3名の組）。表示位置から数えると欠番の分だけレーンがずれるため、必ず
// sort_order そのものから求める（公開側と同じ規則）。
const GROUP_SIZE = 4;
const BASE_MINUTES = 10 * 60;
const groupIndexOf = (cell) => Math.floor((cell - 1) / GROUP_SIZE); // 0始まりの組
const laneOf = (cell) => ((cell - 1) % GROUP_SIZE) + 1; // 1..4
const formatTime = (groupIndex) => {
  if (groupIndex == null || groupIndex < 0) return "";
  const total = BASE_MINUTES + groupIndex;
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

// 地方選抜の対象エリア（SLASH選抜を除く）。応募エリアからトラックを判定する。
const REGIONAL_AREAS = ["大阪", "京都", "兵庫", "奈良"];
const getRegionalAreas = (row) =>
  (row.pickup_areas || []).filter((a) => REGIONAL_AREAS.includes(a));
const hasSlashArea = (row) => (row.pickup_areas || []).includes("SLASH選抜");

// その行の審査が「入力済み」か判定する（未選択=pendingが残っていなければ済み）。
// 1次タブ: 応募した列（地方選抜/SLASH選抜）がすべてpending以外なら済み。
// 2次タブ: 2次結果(second_result)がpending以外なら済み。
const isRowDecided = (row, tab) => {
  if (tab === "second") return (row.second_result || "pending") !== "pending";
  const needsRegional = getRegionalAreas(row).length > 0;
  const needsSlash = hasSlashArea(row);
  const regionalOk =
    !needsRegional || (row.regional_result || "pending") !== "pending";
  const slashOk = !needsSlash || (row.first_result || "pending") !== "pending";
  return regionalOk && slashOk;
};

// 名簿の1行を「3桁番号　氏名(読み仮名)」の形にする（番号と氏名の間は全角スペース、
// 読み仮名の括弧は半角。読み仮名が無ければ省略）。
const rosterLine = (r) => {
  const name = r.name || "（氏名未取得）";
  const yomi = r.name_yomi ? `(${r.name_yomi})` : "";
  return `${pad3(r.no)}　${name}${yomi}`;
};

// 選抜名簿を LINE 共有向けの見やすいテキストにする（タイトル＋3桁番号・氏名・読み仮名）。
const rosterText = (title, list) =>
  [`【${title}】${list.length}名`, ...list.map(rosterLine)].join("\n");

// 2次: 結果ボタン（不合格 / 候補生）。ティア(Tier1〜3)による振り分けは廃止し、
// 合否のみとする。2次合格はHEADZの「候補生」で、最終メンバーはHEADZ最終列で決まる。
// 押されているボタンをもう一度押すと未選択(pending)へ戻す（トグルオフ）。
const SECOND_OPTIONS = [
  { value: "fail", label: "不合格", cls: "fail" },
  { value: "pass", label: "候補生", cls: "pass" },
];

// ティア廃止前のデータ(tier1〜3)はAPI側で 'pass' に読み替えて返すため、
// このページはティアを一切扱わない。

// HEADZ最終: 2次合格者の中から、トレーニング期間を経て実際に出演する人を絞る。
// 2次結果を上書きして絞ると「セクションには受かっていた」事実が消えるため、
// 独立した列(headz_result)で持つ。押し直しで未選択(pending)へ戻す。
const HEADZ_OPTIONS = [
  { value: "fail", label: "見送り", cls: "fail" },
  { value: "pass", label: "HEADZ確定", cls: "pass" },
];

// HEADZのセクション（3つ固定）。HEADZ確定者だけが入り、1人が複数に入ることもある。
// headz_sections にこの value の配列で持つ。並びはこの定義順に揃える。
const HEADZ_SECTIONS = [
  { value: "vocal", label: "VOCAL" },
  { value: "reina", label: "REINA" },
  { value: "yume", label: "YUME" },
];
const getHeadzSections = (row) =>
  Array.isArray(row.headz_sections) ? row.headz_sections : [];

// タブごとの列定義（width は gridTemplateColumns にインライン指定）
// 時間は行ではなく「組の見出し」に出すため、1次に時間列は持たない。
// 代わりに、当日の立ち位置（1列目〜4列目）を置いてジャッジペーパーと突き合わせられるようにする。
// 表記は紙（build_papers_pdf.py の「N列目」）に合わせる。
const FIRST_COLUMNS = [
  { key: "status", label: "", width: "34px", cls: "center status-cell" },
  { key: "no", label: "No", width: "56px", cls: "mono center" },
  { key: "lane", label: "位置", width: "56px", cls: "mono center" },
  { key: "name", label: "氏名", width: "minmax(110px, 1.1fr)", cls: "name" },
  {
    key: "name_yomi",
    label: "フリガナ",
    width: "minmax(90px, 0.9fr)",
    cls: "text",
  },
  {
    key: "regional",
    label: "地方選抜",
    width: "minmax(210px, 1.8fr)",
    cls: "control",
  },
  {
    key: "slash",
    label: "SLASH選抜",
    width: "minmax(160px, 1.1fr)",
    cls: "control slash-col",
  },
];

const SECOND_COLUMNS = [
  { key: "status", label: "", width: "34px", cls: "center status-cell" },
  { key: "no", label: "No", width: "56px", cls: "mono center" },
  { key: "name", label: "氏名", width: "minmax(120px, 1.2fr)", cls: "name" },
  {
    key: "name_yomi",
    label: "フリガナ",
    width: "minmax(100px, 1fr)",
    cls: "text",
  },
  {
    // 地方選抜の結果を参照表示（合格なら「◯◯合格」／それ以外は「-」）
    key: "regional_view",
    label: "地方選抜結果",
    width: "minmax(130px, 1fr)",
    cls: "areas",
  },
  {
    key: "second",
    label: "2次結果",
    width: "minmax(180px, 1.4fr)",
    cls: "control",
  },
  {
    // 2次合格者の中から最終のHEADZ出演者を絞る。2次が合格でない行は操作できない。
    key: "headz",
    label: "HEADZ最終",
    width: "minmax(180px, 1.4fr)",
    cls: "control",
  },
  {
    // HEADZ確定者のセクション（複数選択）。確定していない行は操作できない。
    key: "headz_sections",
    label: "セクション",
    width: "minmax(200px, 1.4fr)",
    cls: "control",
  },
];

// ドラッグハンドル列。並べ替えを有効にしたときだけ差し込む（既定は非表示）。
const GRIP_COLUMN = { key: "grip", label: "", width: "40px", cls: "grip-cell" };
const withGripColumn = (columns) => [columns[0], GRIP_COLUMN, ...columns.slice(1)];

// 並べ替え後の楽観更新用に、位置に合わせて並び順(sort_order)を 1..N で振り直す（immutable）。
// 番号(no)は未ロック時のみ位置に追従させる（ロック中はゼッケンを固定）。
// ※ここで欠番が詰まる＝空きセルが消えるため、並べ替えは既定で無効にしてある。
const reindexOrder = (list, locked) =>
  list.map((r, i) => ({
    ...r,
    sort_order: i + 1,
    ...(locked ? {} : { no: i + 1 }),
  }));

// 並べ替え対応の行（番号確定後も並べ替えは可能なので disabled にしない）
function SortableRow({
  row,
  columns,
  gridTemplateColumns,
  renderCell,
  highlight, // No. 移動でこの行に飛んできたときの一時ハイライト
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: row.id });
  const style = {
    gridTemplateColumns,
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    transition,
    opacity: isDragging ? 0.65 : 1,
    zIndex: isDragging ? 5 : undefined,
  };
  return (
    <div
      ref={setNodeRef}
      className={`grid-row ${highlight ? "is-jumped" : ""}`}
      style={style}
      data-row-no={row.no}
    >
      {columns.map((col) => (
        <div key={col.key} className={`cell ${col.cls || ""}`}>
          {col.key === "grip" ? (
            <span
              className="grip"
              {...attributes}
              {...listeners}
              title="ドラッグで並べ替え"
            >
              <Icon name="bars" />
            </span>
          ) : (
            renderCell(col, row)
          )}
        </div>
      ))}
    </div>
  );
}

function PickupAuditionPage() {
  useLoginCheck();

  const [rows, setRows] = useState([]); // 全員（並び順=sort_order昇順）
  const [orderLocked, setOrderLocked] = useState(false);
  // 会員への公開フラグ。既定は非公開（入力途中が見えないように）
  const [firstPublished, setFirstPublished] = useState(false);
  const [secondPublished, setSecondPublished] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [tab, setTab] = useState("first"); // first | second
  // 審査結果は全パフォーマンス終了後にまとめて入力するため、取りこぼし防止の絞り込みを持つ。
  const [onlyPending, setOnlyPending] = useState(false);
  // 並べ替えは既定で無効。有効化すると sort_order が 1..N に詰められ、
  // 4レーン配置の空きセル（3名の組）が失われるため、明示的に切り替えたときだけ許可する。
  const [reorderEnabled, setReorderEnabled] = useState(false);
  // No. で目的の行へ移動したときの一時ハイライト
  const [highlightNo, setHighlightNo] = useState(null);
  const [jumpNo, setJumpNo] = useState("");
  const gridRef = useRef(null);
  const highlightTimerRef = useRef(null);

  // クリックとドラッグを分離（5px でドラッグ判定）
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 8 },
    }),
  );

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(
        `${apiBase}/api/get_recital2027_pickup_auditions/`,
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const list = Array.isArray(data.rows) ? data.rows : [];
      // 並び順(sort_order)昇順で正規化（同値は no で安定化）
      list.sort(
        (a, b) =>
          (a.sort_order || 0) - (b.sort_order || 0) || (a.no || 0) - (b.no || 0),
      );
      setRows(list);
      setOrderLocked(!!data.order_locked);
      setFirstPublished(!!data.first_published);
      setSecondPublished(!!data.second_published);
    } catch (e) {
      console.error("オーディション一覧の取得に失敗しました:", e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ピックアップ申込の最新状態を取り込み、一覧を更新する。
  // 追加(新規)＝末尾の番号／除外＝取消者(フラグOFF かつ 希望エリア空)を一覧から外す／
  // 復活＝取消後に再申込した人。既存の no は変わらない（新規は後ろの番号を取るだけ）。
  const handleSync = useCallback(async () => {
    try {
      setSyncing(true);
      const res = await fetch(`${apiBase}/api/sync_recital2027_pickup_auditions/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      await fetchData();
      const added = data.added || 0;
      const removed = data.removed || 0;
      const restored = data.restored || 0;
      if (added + removed + restored === 0) {
        alert("変更はありませんでした（最新の状態です）。");
      } else {
        const lines = [];
        if (added > 0) lines.push(`新規申込者 ${added}名を追加しました。`);
        if (restored > 0) lines.push(`再申込 ${restored}名を復活しました。`);
        if (added + restored > 0) {
          lines.push(`（No.${pad3(data.from_no)}〜No.${pad3(data.to_no)}）`);
        }
        if (removed > 0) lines.push(`取消 ${removed}名を一覧から除外しました。`);
        alert(lines.join("\n"));
      }
    } catch (e) {
      console.error("同期に失敗しました:", e);
      alert("同期に失敗しました。");
    } finally {
      setSyncing(false);
    }
  }, [fetchData]);

  // 2次タブ = 1次 pass のみ（rows の並び順=sort_order をそのまま維持）
  const secondRows = useMemo(
    () => rows.filter((r) => r.first_result === "pass"),
    [rows],
  );

  // 行id → { 組(0始まり), レーン(1..4) }。sort_order = セルの絶対位置なので、
  // 空きセル（欠番）があってもレーンがずれない。sort_order 未設定(0以下)の行が
  // 混ざったときだけ、表示位置で詰めて壊れないようにする（公開側と同じ退避）。
  const gridById = useMemo(() => {
    const m = {};
    rows.forEach((r, i) => {
      const cell = r.sort_order > 0 ? r.sort_order : i + 1;
      m[r.id] = { group: groupIndexOf(cell), lane: laneOf(cell) };
    });
    return m;
  }, [rows]);

  // 組ごとの人数（3名の組を見出しに出すため）。絞り込み中でも実際の人数を示したいので
  // 表示中の行ではなく全行から数える。
  const groupCounts = useMemo(() => {
    const m = {};
    rows.forEach((r) => {
      const g = gridById[r.id]?.group;
      if (g == null) return;
      m[g] = (m[g] || 0) + 1;
    });
    return m;
  }, [rows, gridById]);

  // 組ごとの空きレーン。参加者が4の倍数でないぶん3名の組があり、公開タイムテーブルでは
  // 空セルとして出る。管理画面でも同じ位置に空行を置いて見比べられるようにする。
  const emptyLanesByGroup = useMemo(() => {
    const filled = {};
    rows.forEach((r) => {
      const g = gridById[r.id];
      if (!g) return;
      (filled[g.group] = filled[g.group] || new Set()).add(g.lane);
    });
    const m = {};
    Object.entries(filled).forEach(([group, lanes]) => {
      const missing = [];
      for (let lane = 1; lane <= GROUP_SIZE; lane += 1) {
        if (!lanes.has(lane)) missing.push(lane);
      }
      if (missing.length) m[group] = missing;
    });
    return m;
  }, [rows, gridById]);

  // 選抜結果のコピー（LINE共有用）。成功時は一時的に「コピー済」を表示する。
  const [copiedKey, setCopiedKey] = useState("");
  const copyTimerRef = useRef(null);
  useEffect(
    () => () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    },
    [],
  );
  const copyRoster = useCallback(async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopiedKey(""), 1400);
    } catch (e) {
      console.error("コピーに失敗しました:", e);
      alert("コピーに失敗しました。");
    }
  }, []);

  // SLASH選抜（=HEADZ）の最終メンバー一覧。番号順。
  // 2次合格は候補生で、最終オーディションでHEADZ確定(headz_result='pass')になった人だけを載せる。
  const slashPassList = useMemo(
    () =>
      rows
        .filter((r) => r.headz_result === "pass")
        .sort((a, b) => a.no - b.no),
    [rows],
  );

  // HEADZのセクションごとのメンバー（HEADZ確定者のうち、そのセクションに入っている人）。
  const sectionLists = useMemo(
    () =>
      HEADZ_SECTIONS.map((sec) => ({
        ...sec,
        list: slashPassList.filter((r) =>
          getHeadzSections(r).includes(sec.value),
        ),
      })),
    [slashPassList],
  );

  // SLASH選抜を1つのテキストにまとめる。
  const buildSlashText = useCallback(
    () => rosterText("SLASH選抜", slashPassList),
    [slashPassList],
  );

  // 全ての選抜結果（地方4エリア＋SLASH）を1つのテキストにまとめる（該当者のいるものだけ）。
  const buildAllText = useCallback(() => {
    const blocks = [];
    REGIONAL_AREAS.forEach((area) => {
      const list = rows
        .filter((r) => r.regional_result === area)
        .sort((a, b) => a.no - b.no);
      if (list.length) blocks.push(rosterText(`${area}選抜`, list));
    });
    if (slashPassList.length) blocks.push(buildSlashText());
    return blocks.join("\n\n");
  }, [rows, slashPassList, buildSlashText]);

  // 集計: 地方は各エリアの「合格者/参加者」、SLASHは「2次通過/希望」「候補生/2次通過」
  // 「HEADZ確定/候補生」。
  const counts = useMemo(() => {
    const areas = {};
    REGIONAL_AREAS.forEach((a) => (areas[a] = { pass: 0, applied: 0 }));
    let slashHopeful = 0; // SLASH選抜を希望した人（分母: 希望者数）
    let slashPass = 0; // 1次で2次へ通過（first_result='pass'）
    let slashSelected = 0; // 2次で合格＝候補生（second_result='pass'）
    let headzFinal = 0; // 最終でHEADZ確定（headz_result='pass'）
    let headzUnassigned = 0; // HEADZ確定のうちセクション未割当
    rows.forEach((r) => {
      const applied = Array.isArray(r.pickup_areas) ? r.pickup_areas : [];
      REGIONAL_AREAS.forEach((a) => {
        if (applied.includes(a)) {
          areas[a].applied += 1;
          if (r.regional_result === a) areas[a].pass += 1;
        }
      });
      if (applied.includes("SLASH選抜")) {
        slashHopeful += 1;
        if (r.first_result === "pass") slashPass += 1;
        if (r.second_result === "pass") slashSelected += 1;
        if (r.headz_result === "pass") {
          headzFinal += 1;
          if (getHeadzSections(r).length === 0) headzUnassigned += 1;
        }
      }
    });
    return {
      total: rows.length,
      areas,
      slashHopeful,
      slashPass,
      slashSelected,
      headzFinal,
      headzUnassigned,
    };
  }, [rows]);

  // 1次はドラッグハンドル列を「並べ替え有効時のみ」差し込む。
  const columns = useMemo(() => {
    if (tab !== "first") return SECOND_COLUMNS;
    return reorderEnabled ? withGripColumn(FIRST_COLUMNS) : FIRST_COLUMNS;
  }, [tab, reorderEnabled]);
  const gridTemplateColumns = useMemo(
    () => columns.map((c) => c.width).join(" "),
    [columns],
  );
  const gridMinWidth = useMemo(
    () =>
      columns.reduce((sum, c) => {
        const m = /(\d+)px/.exec(c.width);
        return sum + (m ? parseInt(m[1], 10) : 180);
      }, 0) + 40,
    [columns],
  );

  // 合否 / メモの即時保存（楽観更新 → 失敗で再取得）
  const patchRow = useCallback(
    async (id, patch) => {
      const prev = rows;
      setRows((cur) => cur.map((r) => (r.id === id ? { ...r, ...patch } : r)));
      try {
        const res = await fetch(
          `${apiBase}/api/update_recital2027_pickup_audition/`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id, ...patch }),
          },
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || `HTTP ${res.status}`);
        }
      } catch (e) {
        console.error("更新に失敗しました:", e);
        alert(`更新に失敗しました: ${e.message}`);
        setRows(prev); // ロールバック
      }
    },
    [rows],
  );

  // 並べ替え確定（未ロック=no+並び順を振り直し／ロック中=並び順のみ）
  const handleDragEnd = useCallback(
    async (event) => {
      if (!reorderEnabled) return; // 既定は無効（4レーン配置を壊さないため）
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = rows.findIndex((r) => r.id === active.id);
      const newIndex = rows.findIndex((r) => r.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return;

      const prev = rows;
      const moved = reindexOrder(arrayMove(rows, oldIndex, newIndex), orderLocked);
      setRows(moved); // 楽観更新

      try {
        const res = await fetch(
          `${apiBase}/api/reorder_recital2027_pickup_auditions/`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ order: moved.map((r) => r.id) }),
          },
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || `HTTP ${res.status}`);
        }
      } catch (e) {
        console.error("並べ替えに失敗しました:", e);
        alert(`並べ替えに失敗しました: ${e.message}`);
        setRows(prev); // ロールバック
      }
    },
    [rows, orderLocked, reorderEnabled],
  );

  // 番号確定ロックの切替
  const toggleLock = useCallback(async () => {
    const next = !orderLocked;
    if (!next) {
      const ok = window.confirm(
        "ロックを解除すると、並べ替えでゼッケン番号(no)が変わる可能性があります。解除しますか？",
      );
      if (!ok) return;
    }
    try {
      const res = await fetch(`${apiBase}/api/set_recital2027_pickup_lock/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ locked: next }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setOrderLocked(!!data.order_locked);
    } catch (e) {
      console.error("ロック切替に失敗しました:", e);
      alert("ロックの切替に失敗しました。");
    }
  }, [orderLocked]);

  // 会員への公開切替（公開すると会員のタイムテーブルへ即反映される）
  const togglePublish = useCallback(
    async (target) => {
      const cur = target === "first" ? firstPublished : secondPublished;
      const next = !cur;
      const label = target === "first" ? "1次結果" : "2次結果";
      if (next) {
        const ok = window.confirm(
          `${label}を会員に公開します。会員のタイムテーブルに即座に反映されます。よろしいですか？`,
        );
        if (!ok) return;
      }
      try {
        const res = await fetch(`${apiBase}/api/set_recital2027_pickup_publish/`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ target, published: next }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setFirstPublished(!!data.first_published);
        setSecondPublished(!!data.second_published);
      } catch (e) {
        console.error("公開切替に失敗しました:", e);
        alert("公開状態の切替に失敗しました。");
      }
    },
    [firstPublished, secondPublished],
  );

  // セル描画（grip 以外）
  const renderCell = useCallback(
    (col, row) => {
      switch (col.key) {
        case "status": {
          // 入力済み=緑チェック / 未選択=琥珀の中空丸。1次/2次タブで判定基準が変わる。
          return isRowDecided(row, tab) ? (
            <Icon name="check circle" className="st-done" title="入力済み" />
          ) : (
            <Icon name="circle outline" className="st-pending" title="未選択" />
          );
        }
        case "no":
          return pad3(row.no);
        case "lane":
          // 当日の立ち位置。ジャッジペーパーの「N列目」と同じ表記で突き合わせる。
          return gridById[row.id] ? `${gridById[row.id].lane}列目` : "-";
        case "name":
          return row.name || <span className="muted">（氏名未取得）</span>;
        case "name_yomi":
          return row.name_yomi || <span className="muted">—</span>;
        case "regional": {
          // 地方選抜列: 応募した地方エリアがある人のみ。未選択/不合格/各エリア(排他・トグル)。
          const regions = getRegionalAreas(row);
          if (regions.length === 0) return <span className="muted">—</span>;
          const reg = row.regional_result || "pending";
          return (
            <div className="result-btns">
              <button
                className={`rb-btn ${reg === "fail" ? "active fail" : ""}`}
                onClick={() =>
                  patchRow(row.id, {
                    regional_result: reg === "fail" ? "pending" : "fail",
                  })
                }
              >
                不合格
              </button>
              {regions.map((a) => (
                <button
                  key={a}
                  className={`rb-btn ${reg === a ? "active pass" : ""}`}
                  onClick={() =>
                    patchRow(row.id, {
                      regional_result: reg === a ? "pending" : a,
                    })
                  }
                >
                  {a}
                </button>
              ))}
            </div>
          );
        }
        case "slash": {
          // SLASH選抜列: SLASH選抜を応募した人のみ。不合格/2次通過(いずれもトグル)。
          if (!hasSlashArea(row)) return <span className="muted">—</span>;
          const first = row.first_result || "pending";
          return (
            <div className="result-btns">
              <button
                className={`rb-btn ${first === "fail" ? "active fail" : ""}`}
                onClick={() =>
                  patchRow(row.id, {
                    first_result: first === "fail" ? "pending" : "fail",
                  })
                }
              >
                不合格
              </button>
              <button
                className={`rb-btn ${first === "pass" ? "active pass" : ""}`}
                onClick={() =>
                  patchRow(row.id, {
                    first_result: first === "pass" ? "pending" : "pass",
                  })
                }
              >
                2次通過
              </button>
            </div>
          );
        }
        case "regional_view": {
          // 2次タブ用の参照表示: 地方選抜に合格していればそのエリア、
          // 地方に不参加/不合格/未選択なら「-」。
          const v = row.regional_result;
          return REGIONAL_AREAS.includes(v) ? (
            <div className="area-chips">
              <span className="area-chip">{v}合格</span>
            </div>
          ) : (
            <span className="muted">-</span>
          );
        }
        case "second": {
          const v = row.second_result || "pending";
          return (
            <div className="result-btns">
              {SECOND_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  className={`rb-btn ${v === o.value ? `active ${o.cls}` : ""}`}
                  onClick={() =>
                    patchRow(row.id, {
                      // 押されているボタンをもう一度押すと未選択(pending)へ戻す（トグルオフ）。
                      second_result: v === o.value ? "pending" : o.value,
                    })
                  }
                >
                  {o.label}
                </button>
              ))}
            </div>
          );
        }
        case "headz": {
          // HEADZ最終列: 2次合格者のみ操作できる。2次が合格でない行は「—」。
          // サーバー側でも同じ条件を検証している(2次合格者以外は pass にできない)。
          if ((row.second_result || "pending") !== "pass") {
            return <span className="muted">—</span>;
          }
          const v = row.headz_result || "pending";
          return (
            <div className="result-btns">
              {HEADZ_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  className={`rb-btn ${v === o.value ? `active ${o.cls}` : ""}`}
                  onClick={() => {
                    const next = v === o.value ? "pending" : o.value;
                    // 確定を外したらセクションも外す（サーバー側でも同じく空にする）
                    patchRow(row.id, {
                      headz_result: next,
                      ...(next !== "pass" && { headz_sections: [] }),
                    });
                  }}
                >
                  {o.label}
                </button>
              ))}
            </div>
          );
        }
        case "headz_sections": {
          // セクション列: HEADZ確定者のみ。押すたびにそのセクションを付け外しする（複数可）。
          if (row.headz_result !== "pass") {
            return <span className="muted">—</span>;
          }
          const current = getHeadzSections(row);
          return (
            <div className="result-btns">
              {HEADZ_SECTIONS.map((sec) => {
                const on = current.includes(sec.value);
                return (
                  <button
                    key={sec.value}
                    className={`rb-btn ${on ? "active section" : ""}`}
                    aria-pressed={on}
                    onClick={() =>
                      patchRow(row.id, {
                        headz_sections: HEADZ_SECTIONS.map((x) => x.value).filter(
                          (x) => (x === sec.value ? !on : current.includes(x)),
                        ),
                      })
                    }
                  >
                    {sec.label}
                  </button>
                );
              })}
            </div>
          );
        }
        default:
          return row[col.key];
      }
    },
    [patchRow, gridById, tab],
  );

  // 表示中タブの全行（絞り込み前）と、絞り込み後の行。
  const tabRows = tab === "first" ? rows : secondRows;
  // 並べ替え中は絞り込みを効かせない。部分表示のままドラッグすると、
  // 隠れている行を跨いだ移動になり意図しない並びになるため。
  const filterActive = onlyPending && !reorderEnabled;
  const displayRows = useMemo(
    () =>
      filterActive ? tabRows.filter((r) => !isRowDecided(r, tab)) : tabRows,
    [tabRows, filterActive, tab],
  );

  // 1次タブの描画単位。組が変わる境目に見出し（時間・N組目・人数）を差し込み、
  // 3名の組には空きレーンの位置に空行を入れる（公開タイムテーブルの空セルと同じ並び）。
  // 絞り込み中でも見出しは実際の組に対応するので、どの組を見ているか見失わない。
  const firstItems = useMemo(() => {
    if (tab !== "first") return [];
    // 組ごとに束ねる（displayRows は sort_order 昇順なので組も昇順に並ぶ）
    const buckets = [];
    displayRows.forEach((row) => {
      const group = gridById[row.id]?.group ?? 0;
      const last = buckets[buckets.length - 1];
      if (last && last.group === group) last.members.push(row);
      else buckets.push({ group, members: [row] });
    });

    return buckets.flatMap(({ group, members }) => {
      // 空行は実データ基準。絞り込み中は行自体が欠けていて紛らわしいので出さない。
      const empties = filterActive ? [] : emptyLanesByGroup[group] || [];
      const cells = [
        ...members.map((row) => ({
          lane: gridById[row.id]?.lane ?? 0,
          item: { type: "row", key: `r-${row.id}`, row },
        })),
        ...empties.map((lane) => ({
          lane,
          item: { type: "empty", key: `e-${group}-${lane}`, lane },
        })),
      ].sort((a, b) => a.lane - b.lane);
      return [
        { type: "head", key: `g-${group}`, group },
        ...cells.map((c) => c.item),
      ];
    });
  }, [tab, displayRows, gridById, emptyLanesByGroup, filterActive]);

  // No. を入力して該当行へ移動する。見つかったら一時的にハイライトする。
  useEffect(
    () => () => {
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    },
    [],
  );
  const handleJump = useCallback(
    (e) => {
      e.preventDefault();
      const n = parseInt(jumpNo, 10);
      if (!n) return;
      const target = rows.find((r) => r.no === n);
      if (!target) {
        alert(`No.${pad3(n)} は見つかりませんでした。`);
        return;
      }
      // 絞り込みで隠れている行にも飛べるように、必要なら全件表示へ戻す。
      if (onlyPending && isRowDecided(target, tab)) setOnlyPending(false);
      setHighlightNo(n);
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = setTimeout(() => setHighlightNo(null), 2000);
      // 行の描画（絞り込み解除を含む）が終わってからスクロールする。
      requestAnimationFrame(() => {
        const el = gridRef.current?.querySelector(`[data-row-no="${n}"]`);
        if (el) el.scrollIntoView({ block: "center", behavior: "smooth" });
      });
    },
    [jumpNo, rows, onlyPending, tab],
  );

  // 並べ替えの有効/無効を切り替える。有効化は確定済みの配置を壊しうるので確認を挟む。
  const toggleReorder = useCallback(() => {
    if (!reorderEnabled) {
      const ok = window.confirm(
        "並べ替えを有効にします。\n\n" +
          "並べ替えると当日の並び順が 1 番から詰め直され、" +
          "現在の立ち位置（1〜4列目・3名の組の空き）が失われます。\n" +
          "審査員の席順に合わせた配置も崩れます。続けますか？",
      );
      if (!ok) return;
    }
    setReorderEnabled((v) => !v);
  }, [reorderEnabled]);

  // 入力進捗（H案の上部バー用）。表示中タブの一覧に対する「入力済み/全体」。
  const progress = useMemo(() => {
    const list = tab === "second" ? secondRows : rows;
    const total = list.length;
    const done = list.filter((r) => isRowDecided(r, tab)).length;
    return { total, done, pending: total - done };
  }, [rows, secondRows, tab]);

  // 集計バッジをタブごとに出し分ける。frac=分数(合格/母数)。
  // 1次=地方4＋2次通過 / 2次=候補生＋HEADZ確定 / 結果=地方4＋SLASH選抜(HEADZ確定)。
  const summaryBadges = useMemo(() => {
    const regional = REGIONAL_AREAS.map((a) => ({
      key: `area-${a}`,
      cls: "regional",
      label: a,
      value: counts.areas[a].pass,
      total: counts.areas[a].applied,
    }));
    const pass2 = {
      key: "pass2",
      cls: "slash",
      label: "2次通過",
      value: counts.slashPass,
      total: counts.slashHopeful,
    };
    const candidates = {
      key: "candidates",
      cls: "slash",
      label: "候補生",
      value: counts.slashSelected,
      total: counts.slashPass,
    };
    const headzFinal = {
      key: "headz-final",
      cls: "slash",
      label: "SLASH選抜",
      value: counts.headzFinal,
      total: counts.slashSelected,
    };
    // セクションは複数可なので母数は出さない（各セクションの人数と未割当の人数）。
    const sections = [
      ...sectionLists.map((sec) => ({
        key: `section-${sec.value}`,
        cls: "slash",
        label: sec.label,
        value: sec.list.length,
      })),
      {
        key: "section-unassigned",
        cls: "slash",
        label: "セクション未割当",
        value: counts.headzUnassigned,
      },
    ];
    if (tab === "first") return [...regional, pass2];
    // 2次は「2次通過」を出さない（1次タブで確認できるため）。候補生と最終確定の数。
    if (tab === "second") return [candidates, headzFinal, ...sections];
    return [...regional, headzFinal]; // results
  }, [counts, tab, sectionLists]);

  return (
    <div className="recital2027-pickup-audition-page">
      {/* header */}
      <div className="page-header">
        <div className="header-left">
          <p className="eyebrow">Recital 2027 / Pickup Audition</p>
          <div className="title-row">
            <h1>オーディション参加者</h1>
          </div>
        </div>
        <div className="header-right">
          <button className="header-btn" onClick={handleSync} disabled={syncing || loading}>
            <Icon name="sync" loading={syncing} />
            {syncing ? "同期中…" : "申込を同期"}
          </button>
        </div>
      </div>

      {/* tabs ＋ 表示中タブの公開トグル（会員に見せるかどうか） */}
      <div className="tab-row">
        <div className="tab-bar">
          <button
            className={`tab-btn ${tab === "first" ? "active" : ""}`}
            onClick={() => setTab("first")}
          >
            1次
          </button>
          <button
            className={`tab-btn ${tab === "second" ? "active" : ""}`}
            onClick={() => setTab("second")}
          >
            2次
          </button>
          <button
            className={`tab-btn ${tab === "results" ? "active" : ""}`}
            onClick={() => setTab("results")}
          >
            結果
          </button>
        </div>

        <div className="pub-toggles">
          {[
            { key: "first", label: "1次", on: firstPublished },
            { key: "second", label: "2次", on: secondPublished },
          ].map((t) => (
            <div className="pt-item" key={t.key}>
              <span className={`pt-cap ${t.on ? "on" : "off"}`}>
                {t.label} {t.on ? "公開" : "非公開"}
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={t.on}
                aria-label={`${t.label}結果を${t.on ? "非公開に戻す" : "公開する"}`}
                className={`pb-switch ${t.on ? "on" : ""}`}
                onClick={() => togglePublish(t.key)}
              >
                <span className="pb-knob" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* 集計バッジ。タブごとに出し分け（1次=地方＋2次通過 / 2次=候補生＋SLASH選抜 / 結果=地方＋SLASH選抜）。 */}
      <div className="sum-badges">
        {summaryBadges.map((b) => (
          <span key={b.key} className={`sum-badge ${b.cls}`}>
            <span className="sb-label">{b.label}</span>
            <span className="sb-frac">
              <b>{b.value}</b>
              {b.total != null && <i>/{b.total}</i>}
            </span>
          </span>
        ))}
      </div>

      {/* list（1次/2次のテーブル。結果タブでは非表示） */}
      {tab !== "results" && (
      <div className="panel">
        <div className="panel-header">
          <div className="section-title">
            <span className="ja">
              {tab === "first" ? "当日の順番・1次審査" : "2次審査（1次通過者）"}
            </span>
          </div>
          {tab === "first" && (
            <div className="lock-control">
              <button
                className={`lock-btn ${reorderEnabled ? "lock" : "unlock"}`}
                onClick={toggleReorder}
                title="並べ替えると立ち位置（1〜4列目）が詰め直されます"
              >
                <Icon name={reorderEnabled ? "unlock" : "lock"} />
                {reorderEnabled ? "並べ替え中" : "並べ替え無効"}
              </button>
              {orderLocked ? (
                <span className="lock-state locked">
                  <Icon name="lock" />
                  ゼッケン番号 確定済み
                </span>
              ) : (
                <span className="lock-state unlocked">
                  <Icon name="unlock" />
                  番号未確定
                </span>
              )}
              <button
                className={`lock-btn ${orderLocked ? "unlock" : "lock"}`}
                onClick={toggleLock}
              >
                {orderLocked ? "ロック解除" : "番号を確定（ロック）"}
              </button>
            </div>
          )}
        </div>

        {/* ロック中のみバナーを表示（未確定時は非表示） */}
        {tab === "first" && orderLocked && (
          <div className="lock-banner">
            <Icon name="lock" />
            ゼッケン番号は確定済みです。並べ替えても番号は変わりません（当日の順番・時間だけが変わります）。
          </div>
        )}

        {/* 並べ替えを有効にしている間だけ、配置が壊れる注意を出す */}
        {tab === "first" && reorderEnabled && (
          <div className="lock-banner">
            <Icon name="warning sign" />
            並べ替えると当日の並び順が1番から詰め直され、立ち位置（1〜4列目・3名の組の空き）が失われます。
          </div>
        )}

        {/* 入力進捗バー（H案・コンパクト）。「入力済み 選択済み数/全件」＋横並びのバー */}
        {!loading && tabRows.length > 0 && (
          <div className="input-progress">
            <span className="ip-label">
              入力済み <b>{progress.done}</b>/{progress.total}
            </span>
            <div className="ip-bar">
              <div
                className="ip-fill"
                style={{
                  width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%`,
                }}
              />
            </div>
          </div>
        )}

        {/* 入力の道具立て: 未入力だけに絞る／No. で目的の行へ飛ぶ。
            審査は全パフォーマンス終了後にまとめて入力するため、取りこぼしと探し直しを減らす。 */}
        {!loading && tabRows.length > 0 && (
          <div className="list-tools">
            <div className="filter-group" role="group" aria-label="表示の絞り込み">
              <button
                className={`filter-btn ${onlyPending ? "" : "active"}`}
                onClick={() => setOnlyPending(false)}
              >
                全件 {tabRows.length}
              </button>
              <button
                className={`filter-btn ${onlyPending ? "active" : ""}`}
                onClick={() => setOnlyPending(true)}
              >
                未入力のみ {progress.pending}
              </button>
            </div>
            <form className="jump-form" onSubmit={handleJump}>
              <input
                type="number"
                inputMode="numeric"
                min="1"
                placeholder="No."
                value={jumpNo}
                onChange={(e) => setJumpNo(e.target.value)}
                aria-label="ゼッケン番号で移動"
              />
              <button type="submit" className="jump-btn">
                <Icon name="search" />
                移動
              </button>
            </form>
          </div>
        )}

        <div className="panel-body">
          {loading ? (
            <div className="empty-state">
              <p className="ja">読み込み中...</p>
              <p className="en">Loading</p>
            </div>
          ) : displayRows.length === 0 ? (
            <div className="empty-state">
              <p className="ja">
                {filterActive
                  ? "未入力はありません"
                  : tab === "first"
                    ? "参加者がいません"
                    : "2次通過者がいません"}
              </p>
              <p className="en">No data</p>
            </div>
          ) : (
            <div
              className="rc-grid"
              style={{ minWidth: `${gridMinWidth}px` }}
              ref={gridRef}
            >
              <div
                className="grid-row grid-head"
                style={{ gridTemplateColumns }}
              >
                {columns.map((col) => (
                  <div key={col.key} className={`cell ${col.cls || ""}`}>
                    {col.label}
                  </div>
                ))}
              </div>

              {tab === "first" ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={displayRows.map((r) => r.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {firstItems.map((item) =>
                      item.type === "empty" ? (
                        // 3名の組の空きレーン。公開タイムテーブルの空セルと同じ位置に置く。
                        <div
                          className="grid-row is-empty-lane"
                          key={item.key}
                          style={{ gridTemplateColumns }}
                        >
                          {columns.map((col) => (
                            <div
                              key={col.key}
                              className={`cell ${col.cls || ""}`}
                            >
                              {col.key === "lane"
                                ? `${item.lane}列目`
                                : col.key === "name"
                                  ? "空き"
                                  : ""}
                            </div>
                          ))}
                        </div>
                      ) : item.type === "head" ? (
                        // 組の見出し。4人ずつ同時に踊るので、時間は組ごとに1つ。
                        <div className="group-head" key={item.key}>
                          <span className="gh-time">
                            {formatTime(item.group)}
                          </span>
                          <span className="gh-no">{item.group + 1}組目</span>
                          {(groupCounts[item.group] ?? 0) < GROUP_SIZE && (
                            <span className="gh-short">
                              {groupCounts[item.group] ?? 0}名
                            </span>
                          )}
                        </div>
                      ) : (
                        <SortableRow
                          key={item.key}
                          row={item.row}
                          columns={columns}
                          gridTemplateColumns={gridTemplateColumns}
                          renderCell={renderCell}
                          highlight={highlightNo === item.row.no}
                        />
                      ),
                    )}
                  </SortableContext>
                </DndContext>
              ) : (
                displayRows.map((row) => (
                  <div
                    key={row.id}
                    className={`grid-row ${highlightNo === row.no ? "is-jumped" : ""}`}
                    style={{ gridTemplateColumns }}
                    data-row-no={row.no}
                  >
                    {columns.map((col) => (
                      <div key={col.key} className={`cell ${col.cls || ""}`}>
                        {renderCell(col, row)}
                      </div>
                    ))}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
      )}

      {/* 結果タブ: 各地方選抜とSLASH選抜(HEADZ最終確定者)の合格者リスト（SLASH選抜には各人のHEADZセクションを添える） */}
      {tab === "results" && (
        <div className="panel results-panel">
          <div className="panel-header">
            <div className="section-title">
              <span className="ja">審査結果（選抜リスト）</span>
            </div>
            <div className="rl-head-right">
              {(firstPublished || secondPublished) && (
                <span className="panel-hint">
                  公開中の結果は会員にも表示されています
                </span>
              )}
              <button
                className="rl-copy"
                aria-label="全ての選抜結果をコピー"
                title="全ての選抜結果をコピー"
                disabled={
                  REGIONAL_AREAS.every((a) => counts.areas[a].pass === 0) &&
                  counts.headzFinal === 0
                }
                onClick={() => copyRoster("all", buildAllText())}
              >
                <Icon name={copiedKey === "all" ? "check" : "copy"} />
              </button>
            </div>
          </div>
          <div className="panel-body">
            <div className="result-lists">
              {REGIONAL_AREAS.map((area) => {
                const list = rows
                  .filter((r) => r.regional_result === area)
                  .sort((a, b) => a.no - b.no);
                return (
                  <div className="rl-group regional" key={area}>
                    <div className="rl-head">
                      <span className="rl-title">{area}選抜</span>
                      <div className="rl-head-right">
                        <span className="rl-count">{list.length}名</span>
                        <button
                          className="rl-copy"
                          aria-label={`${area}選抜をコピー`}
                          title={`${area}選抜をコピー`}
                          disabled={list.length === 0}
                          onClick={() =>
                            copyRoster(
                              `area-${area}`,
                              rosterText(`${area}選抜`, list),
                            )
                          }
                        >
                          <Icon
                            name={
                              copiedKey === `area-${area}` ? "check" : "copy"
                            }
                          />
                        </button>
                      </div>
                    </div>
                    {list.length ? (
                      <ul className="rl-list">
                        {list.map((r) => (
                          <li key={r.id}>
                            <span className="rl-no">No.{pad3(r.no)}</span>
                            <span className="rl-name">
                              {r.name || "（氏名未取得）"}
                              {r.name_yomi && (
                                <small className="rl-yomi">（{r.name_yomi}）</small>
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="rl-empty">合格者なし</p>
                    )}
                  </div>
                );
              })}

              {/* SLASH選抜(=HEADZ): 最終でHEADZ確定になった人のみ（候補生は載せない） */}
              <div className="rl-group slash">
                <div className="rl-head">
                  <span className="rl-title">SLASH選抜</span>
                  <div className="rl-head-right">
                    <span className="rl-count">{counts.headzFinal}名</span>
                    <button
                      className="rl-copy"
                      aria-label="SLASH選抜をコピー"
                      title="SLASH選抜をコピー"
                      disabled={counts.headzFinal === 0}
                      onClick={() => copyRoster("slash", buildSlashText())}
                    >
                      <Icon name={copiedKey === "slash" ? "check" : "copy"} />
                    </button>
                  </div>
                </div>
                {slashPassList.length === 0 ? (
                  <p className="rl-empty">合格者なし</p>
                ) : (
                  <ul className="rl-list">
                    {slashPassList.map((r) => (
                      <li key={r.id}>
                        <span className="rl-no">No.{pad3(r.no)}</span>
                        <span className="rl-name">
                          {r.name || "（氏名未取得）"}
                          {r.name_yomi && (
                            <small className="rl-yomi">（{r.name_yomi}）</small>
                          )}
                        </span>
                        <span className="rl-sections">
                          {getHeadzSections(r).length ? (
                            HEADZ_SECTIONS.filter((sec) =>
                              getHeadzSections(r).includes(sec.value),
                            ).map((sec) => (
                              <span key={sec.value} className="rl-section">
                                {sec.label}
                              </span>
                            ))
                          ) : (
                            <span className="rl-section unassigned">未割当</span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PickupAuditionPage;
